import React, { useState, useEffect, ChangeEvent } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { addTimecardRecord, adjustTimeCardApi } from "../../../redux/features/EmployeeTimeCard";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router";
import { fetchAttendanceTags, getDepartments, getJobCodes, getSites } from "../../../redux/features/common";
import {
    AddTimecardPayload,
    AdjustTimeCardPayload,
    BreakDuration,
    SelectedTag,
    SelectedTags,
} from "../../../types/state/timecards";
import {
    convertToDateTimeLocalFormat,
    convertToFullDateTime,
    formatDurationStartEndDate,
    convertDecimalHoursToHM,
} from "../../../utils/employeeTimeCardhelper";
import Select from "react-select";
import { CircleX, Loader, Plus } from "lucide-react";
import { setTopNavNameTitle } from "../../../redux/features/mobileLayout";
import AttendanceTagSelector from "./AttendanceTagSelector";
import BreakManagerOverlay from "./BreakManagerOverlay";
import { AttendanceTags } from "../../../types";
import { setGlobalBackHandler, clearGlobalBackHandler } from "../../../hooks";
import { useNavigationHistory } from "../../../hooks/useNavigationHistory";

/**
 * Formats decimal hours to HH:MM format
 * @param hours Decimal hours (e.g., 3.5)
 * @returns Formatted string (e.g., "3:30")
 */
const formatHoursToTimeString = (hours: number): string => {
    // Handle edge cases
    if (isNaN(hours) || hours < 0) {
        return "0:00";
    }

    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);

    // Handle the case where rounding minutes results in 60
    if (minutes === 60) {
        return `${wholeHours + 1}:00`;
    }

    return `${wholeHours}:${minutes.toString().padStart(2, "0")}`;
};

/**
 * Filters break properties based on their type:
 * - Duration-based breaks: include only type and duration_minutes
 * - Time-based breaks: include only type, start_datetime, and end_datetime
 * @param breaks Array of break objects
 * @returns Array of breaks with only the relevant properties for each type
 */
const filterBreaksByType = (breaks: BreakDuration[]): BreakDuration[] => {
    return breaks.map((breakItem) => {
        if (breakItem.duration_minutes !== undefined) {
            // Duration-based break
            return {
                type: breakItem.type,
                duration_minutes: breakItem.duration_minutes,
            };
        } else if (breakItem.start_datetime && breakItem.end_datetime) {
            // Time-based break
            return {
                type: breakItem.type,
                start_datetime: breakItem.start_datetime,
                end_datetime: breakItem.end_datetime,
            };
        }
        // If it's neither, return as is (shouldn't happen in normal operation)
        return breakItem;
    });
};

/**
 * Parses a time input string and converts it to decimal hours
 * @param timeValue Input string, either "HH:MM" format or a plain number
 * @returns Decimal hours or 0 if input is empty
 */
const parseTimeInput = (timeValue: string): number => {
    if (!timeValue) {
        return 0;
    }

    if (timeValue.includes(":")) {
        // Time format (HH:MM)
        const [hours, minutes] = timeValue.split(":");
        const hoursNum = parseInt(hours) || 0;
        const minutesNum = parseInt(minutes) || 0;

        return hoursNum + minutesNum / 60;
    } else {
        // Plain number format
        return Number(timeValue) || 0;
    }
};

interface LocationState {
    recordData?: any;
    mode?: "create" | "edit";
    recordId?: string;
    isManager?: boolean;
    managerView?: {
        department?: string;
        departmentCode?: string;
    };
    managerReturnPath?: string;
    employeeId?: string;
    employeeName?: string;
}

const AddTimeSheetEntryComponent: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { setSourcePath, getSourcePath } = useNavigationHistory();
    const locationState = location.state as LocationState | null;

    // Form States
    const [startDateTime, setStartDateTime] = useState("");
    const [endDateTime, setEndDateTime] = useState("");
    const [site, setSite] = useState("");
    const [department, setDepartment] = useState("");
    const [duration, setDuration] = useState(0);
    const [note, setNote] = useState("");
    const [jobCode, setJobCode] = useState("");
    const [isHoursEntry, setIsHoursEntry] = useState(false);
    const [hoursEntries, setHoursEntries] = useState<{ date: string; hours: number }[]>([{ date: "", hours: 0 }]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [breaks, setBreaks] = useState<BreakDuration[]>([]);

    const [fieldStack, setFieldStack] = useState<(AttendanceTags | null)[]>([]);
    const [localSelectedMap, setLocalSelectedMap] = useState<
        Record<string, { value_code: string; value_text: string }[]>
    >({});

    const [activeTagField, setActiveTagField] = useState<AttendanceTags | null>(null);
    const [showBottomSheet, setShowBottomSheet] = useState(false);

    // State for break manager overlay
    const [showBreakManager, setShowBreakManager] = useState(false);
    const [editBreakIndex, setEditBreakIndex] = useState<number | null>(null);

    // Redux States
    const user = useSelector((state: RootState) => state.user?.user);
    const attendanceOption = user?.mobile_timeclock_option_config?.ATTENDANCE_OPTION;

    const siteOptions = useSelector((state: RootState) => state.common.sites);
    const jobCodeOptions = useSelector((state: RootState) => state.common.jobCodes);
    const attendanceTagsOptions = useSelector((state: RootState) => state.common.attendanceTags);
    const filteredAttendanceTags = attendanceTagsOptions.filter((i) => i?.parent_labor_dimension_id === null);

    const canAddMore = hoursEntries.every((entry) => entry.date.trim() !== "" && entry.hours > 0);
    const jobCodeSelectOptions = jobCodeOptions.map((job) => ({
        value: job.work_code_number,
        label: `${job.work_code_number} - ${job.work_code_name}`,
    }));

    const siteSelectOptions = siteOptions.map((site) => ({
        value: site.us_site_code,
        label: `${site.us_site_code} - ${site.user_site_name}`,
    }));

    const departmentOptions = useSelector((state: RootState) => state.common.departments);
    const departmentSelectOptions = departmentOptions.map((dept: any) => ({
        value: dept.department_code,
        label: `${dept.department_code} - ${dept.department_name}`,
    }));

    // Handlers for datetime inputs
    const handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => setStartDateTime(e.target.value);
    const handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => setEndDateTime(e.target.value);

    // Initialize source path and back handler on mount
    useEffect(() => {
        // If we have location state with a specific path to return to, save it
        if (locationState?.managerReturnPath) {
            setSourcePath(locationState.managerReturnPath);
        } else if (locationState?.isManager) {
            setSourcePath("/m-manager-timesheet");
        } else {
            setSourcePath("/m-timesheet");
        }

        // Set up global back handler - this will always reference the current version of handleBackNavigation
        setGlobalBackHandler(() => {
            handleBackNavigation();
            return true;
        });

        // Clean up when component unmounts
        return () => {
            clearGlobalBackHandler();
        };
    }, [locationState, setSourcePath]);

    // Toggle Hours Entry mode
    const handleToggleHoursEntry = () => {
        setIsHoursEntry((prev) => {
            const newValue = !prev;
            setStartDateTime("");
            setEndDateTime("");
            setDuration(0);
            setHoursEntries(newValue ? [{ date: "", hours: 0 }] : []);
            setSite("");
            setJobCode("");
            setNote("");
            setBreaks([]);
            setLocalSelectedMap({}); // Reset selections
            setFieldStack([]);
            return newValue;
        });
    };

    // Open bottom sheet for a specific field
    const openTagSelector = (field: AttendanceTags) => {
        // Store this field as the root of the stack
        setFieldStack([field]);

        // Initialize the field's selection if it doesn't exist
        setLocalSelectedMap((prev) => ({
            ...prev,
            [field.field_code]: prev[field.field_code] || [],
        }));

        // Recursively ensure that all possible child fields are properly initialized
        const ensureChildFieldsInitialized = (parentField: AttendanceTags) => {
            const childFields = attendanceTagsOptions.filter(
                (tag) => tag.parent_labor_dimension_id === String(parentField.labor_dimensions_id)
            );

            if (childFields.length > 0) {
                setLocalSelectedMap((prev) => {
                    const updated = { ...prev };
                    childFields.forEach((child) => {
                        if (!updated[child.field_code]) {
                            updated[child.field_code] = [];
                        }
                    });
                    return updated;
                });

                // Recursively ensure their children are initialized too
                childFields.forEach((child) => ensureChildFieldsInitialized(child));
            }
        };

        ensureChildFieldsInitialized(field);

        // In edit mode, if we have selections for this field, we need to set up the field stack properly
        const isEditMode = locationState?.mode === "edit";
        if (isEditMode && locationState?.recordData) {
            // Use recordData directly to verify tag selections
            const recordData = locationState.recordData;
            if (recordData.tags && Array.isArray(recordData.tags)) {
                // Find tags that match our current field
                const fieldTag = recordData.tags.find((tag: any) => tag.field_code === field.field_code);

                if (fieldTag && fieldTag.field_values && fieldTag.field_values.length > 0) {
                    // We have confirmed selections from recordData, now find child selections
                    const buildFieldStack = (currentField: AttendanceTags): AttendanceTags[] => {
                        const stack = [currentField];

                        // Look for child fields that have selections based on recordData
                        const childFields = attendanceTagsOptions.filter(
                            (tag) => tag.parent_labor_dimension_id === String(currentField.labor_dimensions_id)
                        );

                        for (const childField of childFields) {
                            // Check if this child has selections in recordData
                            const childFieldTag = recordData.tags.find(
                                (tag: any) => tag.field_code === childField.field_code
                            );

                            if (childFieldTag && childFieldTag.field_values && childFieldTag.field_values.length > 0) {
                                // This child has selections, add it and its descendants to the stack
                                const childStack = buildFieldStack(childField);
                                return [...stack, ...childStack];
                            }
                        }

                        return stack;
                    };

                    // Update field stack with the complete path of selected fields from recordData
                    const fullStack = buildFieldStack(field);
                    if (fullStack.length > 1) {
                        // Only update if we found children
                        setFieldStack(fullStack);
                    }
                }
            }
        }

        setActiveTagField(field);
        setShowBottomSheet(true);
    };

    // Handler called by AttendanceTagSelector on selection changes for current root field
    const handleTagOptionSelect = (
        rootFieldCode: string,
        selected: { value_code: string; value_text: string }[],
        allSelections?: {
            field_code: string;
            field_values: { value_code: string; value_text: string }[];
        }[]
    ) => {
        // If we received all selections with the new format, use that
        if (allSelections && allSelections.length > 0) {
            // Reset the map and add all the new selections
            const newMap: Record<string, { value_code: string; value_text: string }[]> = {};

            // Add each field's selections to the map
            allSelections.forEach((item) => {
                newMap[item.field_code] = item.field_values;
            });

            // Replace the entire selection map
            setLocalSelectedMap(newMap);
        } else {
            // Fall back to the old behavior if allSelections wasn't provided
            setLocalSelectedMap((prev) => ({
                ...prev,
                [rootFieldCode]: selected,
            }));
        }
    };

    function buildTagsPayload(
        localSelectedMap: Record<string, { value_code: string; value_text: string }[]>,
        attendanceTags: AttendanceTags[]
    ): SelectedTags[] {
        // First identify root tags (those without parents or with null parent)
        const rootFields = attendanceTags.filter(
            (tag) => tag.parent_labor_dimension_id === null || tag.parent_labor_dimension_id === ""
        );

        const rootFieldCodes = rootFields.map((field) => field.field_code);

        // Build a map of fields by their IDs for quick lookup
        const fieldsById = attendanceTags.reduce((acc, tag) => {
            acc[String(tag.labor_dimensions_id)] = tag;
            return acc;
        }, {} as Record<string, AttendanceTags>);

        // Build a map of parent-child relationships
        const childrenByParentId: Record<string, AttendanceTags[]> = {};
        attendanceTags.forEach((tag) => {
            if (tag.parent_labor_dimension_id) {
                if (!childrenByParentId[tag.parent_labor_dimension_id]) {
                    childrenByParentId[tag.parent_labor_dimension_id] = [];
                }
                childrenByParentId[tag.parent_labor_dimension_id].push(tag);
            }
        });

        // Filter out any local selections that aren't in our map or don't have values
        const validSelections = Object.entries(localSelectedMap).filter(([_, values]) => values && values.length > 0);

        // Split into root selections and child selections
        const rootSelections = validSelections.filter(([field_code]) => rootFieldCodes.includes(field_code));

        // Start with root tags
        const result: SelectedTags[] = rootSelections.map(([field_code, values]) => ({
            field_code,
            field_values: values.map((v) => ({
                value_code: v.value_code,
                value_text: v.value_text,
            })),
        }));

        console.log("result :: ", result);

        // If no root selections, return empty array
        if (result.length === 0) {
            return [];
        }

        // Store all non-root selections, organized by their level in the hierarchy
        const findFieldLevelAndPath = (fieldCode: string): { level: number; path: string[] } => {
            // Find the field by its code
            const field = attendanceTags.find((t) => t.field_code === fieldCode);
            if (!field) return { level: 0, path: [] };

            // If it's a root field, return level 1
            if (!field.parent_labor_dimension_id) {
                return { level: 1, path: [field.field_code] };
            }

            // Otherwise, find its parent and add 1 to the parent's level
            let current = field;
            const path = [fieldCode];
            let level = 1;

            // Traverse up the hierarchy until we reach a root field
            while (current.parent_labor_dimension_id) {
                const parentField = attendanceTags.find(
                    (t) => String(t.labor_dimensions_id) === current.parent_labor_dimension_id
                );

                if (!parentField) break;

                path.unshift(parentField.field_code);
                current = parentField;
                level++;
            }

            return { level, path };
        };

        // Group non-root selections by their hierarchy level and path
        const nonRootSelections = validSelections
            .filter(([field_code]) => !rootFieldCodes.includes(field_code))
            .map(([field_code, values]) => {
                const { level, path } = findFieldLevelAndPath(field_code);
                return {
                    field_code,
                    values,
                    level,
                    path,
                };
            })
            // Sort by level to process in order from higher levels to lower levels
            .sort((a, b) => a.level - b.level);

        // Add non-root selections as separate items in the result array
        // instead of nesting them under their parent tags
        if (nonRootSelections.length > 0) {
            nonRootSelections.forEach(({ field_code, values }) => {
                // Find the tag info for this field
                const tagInfo = attendanceTags.find((t) => t.field_code === field_code);

                if (tagInfo) {
                    // Add the child selection as a separate entry in the result
                    result.push({
                        field_code,
                        field_values: values.map((value) => ({
                            value_code: value.value_code,
                            value_text: value.value_text,
                        })),
                    });
                }
            });
        }

        return result;
    }

    // Enhanced form validation
    const validateForm = (): boolean => {
        /**
         * removing validation for site, department and job code
         * @vishwesh5544
         */
        // Require site, department and job code selections
        // if (!site) {
        //     toast.error("Please select a site");
        //     return false;
        // } else if (!department) {
        //     toast.error("Please select a department");
        //     return false;
        // } else if (!jobCode) {
        //     toast.error("Please select a job code");
        //     return false;
        // }

        // Validate Hours entries if in hours mode
        if (isHoursEntry) {
            // Check if entries exist
            if (!hoursEntries.length) {
                toast.error("Please add at least one time entry");
                return false;
            }

            const today = new Date();
            const maxDate = new Date();
            maxDate.setFullYear(today.getFullYear() + 1); // Allow dates up to 1 year in the future

            // More comprehensive validation for each entry
            for (let i = 0; i < hoursEntries.length; i++) {
                const entry = hoursEntries[i];

                // Check for empty date
                if (!entry.date) {
                    toast.error(`Please enter a date for entry ${i + 1}`);
                    return false;
                }

                // Check for valid date range (not too far in the past or future)
                const entryDate = new Date(entry.date);
                const minDate = new Date();
                minDate.setFullYear(today.getFullYear() - 1); // Allow dates up to 1 year in the past

                if (entryDate < minDate) {
                    toast.error(`Date for entry ${i + 1} is too far in the past`);
                    return false;
                }

                if (entryDate > maxDate) {
                    toast.error(`Date for entry ${i + 1} is too far in the future`);
                    return false;
                }

                // Check for valid hours
                if (entry.hours <= 0) {
                    toast.error(`Please enter valid hours for entry ${i + 1}`);
                    return false;
                }

                if (entry.hours > 24) {
                    toast.error(`Hours for entry ${i + 1} cannot exceed 24 hours`);
                    return false;
                }
            }

            // Check for duplicate dates
            const dateSet = new Set<string>();
            for (const entry of hoursEntries) {
                if (dateSet.has(entry.date)) {
                    toast.error("Duplicate dates found. Please use a single entry per date");
                    return false;
                }
                dateSet.add(entry.date);
            }
        } else {
            // Validate date range for duration entry
            if (!startDateTime) {
                toast.error("Please enter valid start datetime");
                return false;
            }

            /**
             * removing validation for endDateTime
             * @vishwesh5544
             */
            // if (!endDateTime) {
            //     toast.error("Please enter valid end datetime");
            //     return false;
            // }

            // Calculate duration in hours
            const startDate = new Date(startDateTime);
            const endDate = new Date(endDateTime);

            if (endDate <= startDate) {
                toast.error("End time must be after start time");
                return false;
            }

            const durationMs = endDate.getTime() - startDate.getTime();
            const durationHours = durationMs / (1000 * 60 * 60);

            if (durationHours > 24) {
                toast.error("Duration cannot exceed 24 hours");
                return false;
            }
        }

        // Validate required tags
        for (const field of filteredAttendanceTags) {
            const isRequired = field?.other_info_json?.required;
            if (isRequired) {
                const selected = localSelectedMap[field.field_code];
                if (!selected || selected.length === 0) {
                    toast.error(`Please select a value for "${field.field_name}"`);
                    return false;
                }
            }
        }

        return true;
    };

    // Submit handler
    // const handleSubmit = () => {
    //   if (!validateForm()) return;

    //   // Build tags payload if needed
    //   // const tags = ... convert localSelectedMap to payload as required

    //   const payload: AddTimecardPayload = isHoursEntry
    //     ? {
    //         employee_id: String(user.id),
    //         entry_type: "HOURS",
    //         site_code: site,
    //         work_code: jobCode,
    //         tags: null, // replace with actual tags if needed
    //         notes: note,
    //         hours_entries: hoursEntries,
    //       }
    //     : {
    //         employee_id: String(user.id),
    //         entry_type: "DURATION",
    //         site_code: site,
    //         work_code: jobCode,
    //         tags: null, // replace with actual tags if needed
    //         notes: note,
    //         duration_entry: {
    //           start_datetime: convertToFullDateTime(startDateTime),
    //           end_datetime: convertToFullDateTime(endDateTime),
    //           breaks: [],
    //         },
    //       };

    //   dispatch(addTimecardRecord(payload))
    //     .unwrap()
    //     .then((res) => {
    //       toast.success(res.message || "Timecard entry added successfully!");
    //       navigate("/m-timesheet");
    //     })
    //     .catch((err) => {
    //       toast.error(err.message || "Failed to add timecard. Please try again.");
    //     });
    // };

    const handleSubmit = () => {
        if (!validateForm()) return;

        const tagsPayload = buildTagsPayload(localSelectedMap, attendanceTagsOptions);

        const isEditMode = locationState?.mode === "edit";

        // Set submitting state to display loading spinner
        setSubmitting(true);

        if (isEditMode && locationState?.recordId) {
            // For edit mode, use adjustTimeCardApi
            const adjustPayload: AdjustTimeCardPayload = {
                record_id: locationState.recordId,
                employee_id: locationState?.employeeId,
                entry_type: "DURATION",
                duration_entry: {
                    start_datetime: convertToFullDateTime(startDateTime),
                    end_datetime: convertToFullDateTime(endDateTime),
                    breaks: filterBreaksByType(breaks), // Include filtered breaks in edit payload
                },
                department_code: department,
                site_code: site,
                work_code: jobCode,
                notes: note,
                // Convert tagsPayload to the format expected by adjustTimeCardApi
                tags: tagsPayload?.length
                    ? tagsPayload.flatMap((tag) =>
                          tag.field_values.map((value) => ({
                              code: tag.field_code,
                              value: value.value_text,
                          }))
                      )
                    : null,
            };

            dispatch(adjustTimeCardApi(adjustPayload))
                .unwrap()
                .then((res) => {
                    toast.success("Timecard updated successfully!");
                    // Use managerReturnPath if available
                    if (locationState?.managerReturnPath) {
                        navigate(locationState.managerReturnPath);
                    } else {
                        navigate(`/m-timecard-record-detail/${locationState.recordId}`);
                    }
                })
                .catch((err) => {
                    console.error("Error updating timecard:", err);
                    toast.error(err.message || "Failed to update timecard. Please try again.");
                })
                .finally(() => setSubmitting(false));
        } else {
            // For add mode, use addTimecardRecord
            const payload: AddTimecardPayload = isHoursEntry
                ? {
                      employee_id: locationState?.employeeId,
                      entry_type: "HOURS",
                      site_code: site,
                      work_code: jobCode,
                      department_code: department,
                      tags: tagsPayload.length ? tagsPayload : null,
                      notes: note,
                      hours_entries: hoursEntries.map((entry) => ({
                          ...entry,
                          breaks: filterBreaksByType(breaks), // Filter breaks by type
                      })),
                  }
                : {
                      employee_id: locationState?.employeeId,
                      entry_type: "DURATION",
                      site_code: site,
                      work_code: jobCode,
                      department_code: department,
                      tags: tagsPayload.length ? tagsPayload : null,
                      notes: note,
                      duration_entry: {
                          start_datetime: convertToFullDateTime(startDateTime),
                          end_datetime: convertToFullDateTime(endDateTime),
                          breaks: filterBreaksByType(breaks), // Filter breaks by type
                      },
                  };

            dispatch(addTimecardRecord(payload))
                .unwrap()
                .then((res) => {
                    toast.success(res.message || "Timecard entry added successfully!");
                    // Use managerReturnPath if available
                    if (locationState?.managerReturnPath) {
                        navigate(locationState.managerReturnPath);
                    } else {
                        navigate("/m-timesheet");
                    }
                })
                .catch((err) => {
                    console.error("Error adding timecard:", err);
                    toast.error(err.message || "Failed to add timecard. Please try again.");
                })
                .finally(() => setSubmitting(false));
        }
    };

    // Handle back button press
    const handleBackNavigation = () => {
        // Try to navigate using the navigation history
        const sourcePath = getSourcePath();

        if (sourcePath) {
            // We have a recorded source path, use it
            navigate(sourcePath);
        }
        // Fallbacks if source path isn't available
        else if (locationState?.managerReturnPath) {
            navigate(locationState.managerReturnPath);
        } else if (locationState?.isManager) {
            navigate("/m-manager-timesheet");
        } else {
            navigate("/m-timesheet");
        }
    };

    useEffect(() => {
        const isEditMode = locationState?.mode === "edit";
        dispatch(setTopNavNameTitle(isEditMode ? "Edit TimeCard Entry" : "Add TimeCard Entry"));

        // Set up back button behavior in the header
        const handleBackButton = () => {
            handleBackNavigation();
            return true; // Prevent default back behavior
        };

        // Add event listener for hardware back button (mobile devices)
        window.addEventListener("popstate", handleBackButton);

        // Clean up event listener when component unmounts
        return () => {
            window.removeEventListener("popstate", handleBackButton);
        };
    }, [navigate]);

    // We've replaced navigation to BreakManagerPage with an overlay component
    // So we don't need to handle returning from that page anymore

    useEffect(() => {
        setLoading(true);
        Promise.all([
            dispatch(getSites()),
            dispatch(getDepartments()),
            dispatch(getJobCodes()),
            dispatch(fetchAttendanceTags()),
        ]).then(() => {
            // If in edit mode, populate the form with existing data
            const isEditMode = locationState?.mode === "edit";
            if (locationState?.managerView?.departmentCode) {
                setDepartment(locationState.managerView.departmentCode);
            }
            if (isEditMode && locationState?.recordData) {
                const recordData = locationState.recordData;

                // Set start and end date/time
                if (recordData.os_emp_start_datetime) {
                    setStartDateTime(convertToDateTimeLocalFormat(recordData.os_emp_start_datetime));
                }

                if (recordData.os_emp_end_datetime) {
                    setEndDateTime(convertToDateTimeLocalFormat(recordData.os_emp_end_datetime));
                }

                // Set department
                if (recordData.department?.code) {
                    setDepartment(recordData.department.code);
                }

                // Set site
                if (recordData.site?.code) {
                    setSite(recordData.site.code);
                }

                // Set job code
                if (recordData.work_code?.code) {
                    setJobCode(recordData.work_code.code);
                }

                // Set note if available
                if (recordData.notes) {
                    setNote(recordData.notes);
                }

                // Load breaks if available
                if (recordData.breaktimes_json && recordData.breaktimes_json.breaktimes) {
                    try {
                        // Convert from Breaktime format to BreakDuration format with duration
                        const loadedBreaks: BreakDuration[] = recordData.breaktimes_json.breaktimes.map(
                            (breakItem: any) => {
                                // Calculate duration in minutes if start and end times are available
                                let duration_minutes = 0;
                                if (breakItem.start_datetime && breakItem.end_datetime) {
                                    const startTime = new Date(breakItem.start_datetime);
                                    const endTime = new Date(breakItem.end_datetime);
                                    duration_minutes = Math.round(
                                        (endTime.getTime() - startTime.getTime()) / (1000 * 60)
                                    );
                                }

                                return {
                                    type: breakItem.is_paid ? "paid" : "unpaid",
                                    duration_minutes: duration_minutes,
                                };
                            }
                        );
                        setBreaks(loadedBreaks);
                    } catch (error) {
                        console.error("Error loading breaks data:", error);
                    }
                }

                console.log("has attendance_tags:", recordData);
                // Set tags if available - this is important to properly initialize the tag selections
                if (recordData.attendence_tags && Array.isArray(recordData.attendence_tags)) {
                    const newSelectedMap: Record<string, { value_code: string; value_text: string }[]> = {};
                    console.log("Loaded existing record data:", recordData);

                    // Process each tag and add to the selection map
                    recordData.attendence_tags.forEach(
                        (tag: {
                            field_code: string;
                            field_values: Array<{ value_code: string; value_text: string }>;
                        }) => {
                            if (tag.field_code && Array.isArray(tag.field_values)) {
                                newSelectedMap[tag.field_code] = tag.field_values.map(
                                    (value: { value_code: string; value_text: string }) => ({
                                        value_code: value.value_code,
                                        value_text: value.value_text,
                                    })
                                );
                            }
                        }
                    );

                    console.log("Loaded existing tag selections:", newSelectedMap);

                    // Update the local selected map with the existing values
                    setLocalSelectedMap(newSelectedMap);
                }
            }
            setLoading(false);
        });
    }, [dispatch, locationState, convertToDateTimeLocalFormat]);

    // Enhanced breadcrumb builder that handles all tag scenarios
    function getSelectedBreadcrumb(
        field: AttendanceTags,
        selectedMap: Record<string, { value_code: string; value_text: string }[]>,
        allFields: AttendanceTags[]
    ): string {
        const selectedValues = selectedMap[field.field_code];
        if (!selectedValues || selectedValues.length === 0) return "";

        // Build a unified breadcrumb path that handles both single and multi-select
        const buildBreadcrumb = (
            startField: AttendanceTags,
            selMap: Record<string, { value_code: string; value_text: string }[]>
        ): string => {
            const selected = selMap[startField.field_code];
            if (!selected || selected.length === 0) return "";

            // Handle multiple selections with comma separation
            let value: string;
            if (startField.multiselect === "true" && selected.length > 1) {
                value = selected.map((v) => v.value_text).join(", ");
            } else {
                value = selected[0].value_text;
            }

            // Check for child selections - scan all possible child fields
            const childFields = allFields.filter(
                (f) => f.parent_labor_dimension_id === String(startField.labor_dimensions_id)
            );

            // If we have child fields with selections, build their breadcrumbs
            if (childFields.length > 0) {
                for (const childField of childFields) {
                    const childSelected = selMap[childField.field_code];
                    if (childSelected && childSelected.length > 0) {
                        const childBreadcrumb = buildBreadcrumb(childField, selMap);
                        if (childBreadcrumb) {
                            return `${value} > ${childBreadcrumb}`;
                        }
                    }
                }
            }

            return value;
        };

        // Create a full breadcrumb for this field hierarchy
        const breadcrumb = buildBreadcrumb(field, selectedMap);

        // For edit mode, ensure we build full paths that match the JSON from the API
        const isEditMode = locationState?.mode === "edit";
        if (isEditMode) {
            const getFullPath = (field: AttendanceTags): AttendanceTags[] => {
                const path = [field];

                // Loop through the hierarchy, finding child fields that have selections
                let currentField = field;
                let foundChild = true;

                while (foundChild) {
                    foundChild = false;
                    const childFields = allFields.filter(
                        (f) => f.parent_labor_dimension_id === String(currentField.labor_dimensions_id)
                    );

                    for (const childField of childFields) {
                        const childSelected = selectedMap[childField.field_code];
                        if (childSelected && childSelected.length > 0) {
                            path.push(childField);
                            currentField = childField;
                            foundChild = true;
                            break; // We've found a child with selections, move to the next level
                        }
                    }
                }

                return path;
            };

            // If there's more than just the root field, ensure the full path is reflected in the breadcrumb
            const fullPath = getFullPath(field);
            if (fullPath.length > 1) {
                return breadcrumb; // The buildBreadcrumb function already handles this correctly
            }
        }

        return breadcrumb;
    }

    return (
        <div className="max-w-full mx-4 space-y-4 mt-4 overflow-y-auto h-[calc(100vh-80px)]">
            {loading ? (
                <div className="flex justify-center items-center py-4">
                    <Loader size={32} color="#2664eb" strokeWidth={2.5} className="animate-spin" />
                </div>
            ) : (
                <>
                    {/* <div className="flex items-center mb-2">
            <button 
              onClick={handleBackNavigation}
              className="flex items-center text-blue-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Timesheet
            </button>
          </div> */}

                    <div className="flex justify-between items-center">
                        <label className="cursor-pointer font-medium">{isHoursEntry ? "Hours " : "Duration"}</label>
                        {locationState?.mode !== "edit" && (
                            <button
                                type="button"
                                role="switch"
                                aria-checked={isHoursEntry}
                                onClick={handleToggleHoursEntry}
                                className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
                                    isHoursEntry ? "bg-blue-600" : "bg-gray-300"
                                }`}
                            >
                                <span
                                    className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                                        isHoursEntry ? "translate-x-6" : "translate-x-1"
                                    }`}
                                />
                            </button>
                        )}
                    </div>

                    {!isHoursEntry ? (
                        <>
                            <label className="block text-md font-medium">Start Date & Time</label>
                            <input
                                required
                                type="datetime-local"
                                value={startDateTime}
                                onChange={handleStartDateChange}
                                className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
                                placeholder="Start Date & Time"
                            />

                            <label className="block text-md font-medium mt-4">End Date & Time</label>
                            <input
                                type="datetime-local"
                                value={endDateTime}
                                onChange={handleEndDateChange}
                                min={startDateTime}
                                className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
                                placeholder="End Date & Time"
                            />

                            <div className="flex justify-between mt-4">
                                <label className="block text-md font-medium">Duration</label>
                                <span>{formatDurationStartEndDate(startDateTime, endDateTime) || "0"}</span>
                            </div>
                        </>
                    ) : (
                        <>
                            {hoursEntries.map((entry, index) => (
                                <div key={index} className="flex items-center space-x-2 mb-2">
                                    <div className="flex-1">
                                        <label className="block text-md font-medium">Date</label>
                                        <div className="relative">
                                            <input
                                                type="date"
                                                value={entry.date}
                                                onChange={(e) => {
                                                    const selectedDate = e.target.value;
                                                    const newEntries = [...hoursEntries];

                                                    // Apply some basic validation
                                                    if (selectedDate) {
                                                        const date = new Date(selectedDate);
                                                        const today = new Date();
                                                        const oneYearAgo = new Date();
                                                        oneYearAgo.setFullYear(today.getFullYear() - 1);
                                                        const oneYearFromNow = new Date();
                                                        oneYearFromNow.setFullYear(today.getFullYear() + 1);

                                                        if (date < oneYearAgo) {
                                                            toast.warn("Date cannot be more than 1 year in the past");
                                                            return;
                                                        }

                                                        if (date > oneYearFromNow) {
                                                            toast.warn("Date cannot be more than 1 year in the future");
                                                            return;
                                                        }
                                                    }

                                                    newEntries[index].date = selectedDate;
                                                    setHoursEntries(newEntries);

                                                    // Check for duplicate dates
                                                    const dateCount = hoursEntries.filter(
                                                        (e) => e.date === selectedDate
                                                    ).length;
                                                    if (dateCount > 1) {
                                                        toast.warn(
                                                            "This date already has an entry. Please use a different date."
                                                        );
                                                    }
                                                }}
                                                className="w-full mt-1 p-3 border-2 border-b border-gray-300 rounded-md text-gray-500"
                                                placeholder="Date"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-md font-medium">Hours</label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={entry.hours > 0 ? formatHoursToTimeString(entry.hours) : ""}
                                                onChange={(e) => {
                                                    const timeValue = e.target.value;
                                                    const newEntries = [...hoursEntries];

                                                    // Simple conversion without validation warnings
                                                    newEntries[index].hours = parseTimeInput(timeValue);
                                                    setHoursEntries(newEntries);
                                                }}
                                                className="w-full mt-1 p-3 border-2 border-b border-gray-300 rounded-md text-gray-700"
                                                placeholder="Hours (e.g. 3:30)"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="text-red-500 font-bold mt-6"
                                        onClick={() => {
                                            // Remove this entry row
                                            const newEntries = hoursEntries.filter((_, i) => i !== index);
                                            setHoursEntries(newEntries.length ? newEntries : [{ date: "", hours: 0 }]);
                                        }}
                                        aria-label="Remove entry"
                                    >
                                        {/* &times; */}
                                        <CircleX />
                                    </button>
                                </div>
                            ))}

                            <button
                                type="button"
                                className={`px-4 py-1  border-solid border-2  border-blue-600 hover:border-white-600 rounded-md bg-blue-600 font-extrabold  ${
                                    canAddMore
                                        ? "text-blue-600 hover:text-blue-700 hover:bg-gray-200 hover:text-white-200"
                                        : "text-gray-400 cursor-not-allowed"
                                }`}
                                onClick={() => {
                                    if (!canAddMore) {
                                        // Show validation message if entries are incomplete
                                        const emptyDateEntries = hoursEntries.filter((e) => !e.date.trim());
                                        const zeroHourEntries = hoursEntries.filter((e) => e.hours <= 0);

                                        if (emptyDateEntries.length > 0) {
                                            toast.error("Please enter a date for all entries");
                                        } else if (zeroHourEntries.length > 0) {
                                            toast.error("Please enter hours for all entries");
                                        }
                                        return;
                                    }
                                    setHoursEntries([...hoursEntries, { date: "", hours: 0 }]);
                                }}
                                aria-label="Add another time entry"
                                title="Add another time entry"
                            >
                                <Plus size={20} color="white" strokeWidth={3} />
                            </button>
                        </>
                    )}

                    {/* Break Manager for Duration Entry - Always visible */}
                    <div className="rounded-lg">
                        <div className="flex justify-between items-center mb-1">
                            <label className="text-gray-800 text-md font-medium">Breaks</label>
                            {/* <span className="text-md font-bold">{breaks.length > 0 ? "3hr 0min" : ""}</span> */}
                        </div>

                        {/* Break Summary */}
                        {breaks.length > 0 ? (
                            <div className="mt-2">
                                {breaks.map((breakItem, idx) => (
                                    <div key={idx} className="flex justify-between items-center mb-3">
                                        <div>
                                            <div className="text-gray-800">12:30 PM - 12:45 PM</div>
                                            <div className="text-xs text-gray-500">
                                                {breakItem.duration_minutes} min •{" "}
                                                {breakItem.type === "paid" ? "Paid" : "Unpaid"}
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // Open break manager overlay in edit mode
                                                    setEditBreakIndex(idx);
                                                    setShowBreakManager(true);
                                                }}
                                                className="text-blue-600"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updatedBreaks = [...breaks];
                                                    updatedBreaks.splice(idx, 1);
                                                    setBreaks(updatedBreaks);
                                                }}
                                                className="text-red-600"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}

                        <button
                            type="button"
                            onClick={() => {
                                setEditBreakIndex(null);
                                setShowBreakManager(true);
                            }}
                            className="text-blue-600 mt-3 flex items-center"
                        >
                            + Add Break
                        </button>
                    </div>

                    {attendanceOption?.code && (
                        <div>
                            <label className="text-gray-800 text-md font-medium mt-4">Code</label>
                            <Select
                                value={jobCodeSelectOptions.find((option) => option.value === jobCode) || null}
                                onChange={(selectedOption: any) => setJobCode(selectedOption?.value)}
                                placeholder="Select Job Code"
                                options={jobCodeSelectOptions}
                                className="mt-1"
                            />
                        </div>
                    )}

                    {attendanceOption?.site && (
                        <div>
                            <label className="text-gray-800 text-md font-medium mt-4">Site</label>
                            <Select
                                value={siteSelectOptions.find((option) => option.value === site) || null}
                                onChange={(selectedOption: any) => setSite(selectedOption?.value)}
                                placeholder="Select Site"
                                options={siteSelectOptions}
                                className="mt-1"
                            />
                        </div>
                    )}

                    {attendanceOption?.department && (
                        <div>
                            <label className="text-gray-800 text-md font-medium mt-4">Department</label>
                            <Select
                                value={departmentSelectOptions.find((option) => option.value === department) || null}
                                onChange={(selectedOption: any) => setDepartment(selectedOption?.value)}
                                placeholder="Select Department"
                                options={departmentSelectOptions}
                                className="mt-1"
                            />
                        </div>
                    )}

                    {/* Render attendance tag buttons */}
                    {attendanceOption?.tags && filteredAttendanceTags.map((field) => {
                        if (field.field_type === "Dropdown") {
                            return (
                                <div key={field.labor_dimensions_id} className="mt-4">
                                    <label className="block font-semibold mb-1">
                                        {field.field_name}{" "}
                                        {field?.other_info_json?.required && <span className="text-red-500">*</span>}
                                    </label>

                                    <button
                                        type="button"
                                        onClick={() => openTagSelector(field)}
                                        className="w-full p-3 text-left border border-gray-300 rounded-md"
                                    >
                                        {getSelectedBreadcrumb(field, localSelectedMap, attendanceTagsOptions) ||
                                            ` ${field.field_name}`}
                                    </button>
                                </div>
                            );
                        }

                        // ... your existing Toggle and Text fields logic here ...
                        //             // For Toggle and Text fields, you can keep your existing inputs or convert similarly
                        if (field.field_type === "Toggle") {
                            const checked = localSelectedMap[field.field_code]?.[0]?.value_text === "1";
                            return (
                                <div key={field.labor_dimensions_id} className="mt-4">
                                    <label className="inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => {
                                                setLocalSelectedMap((prev) => ({
                                                    ...prev,
                                                    [field.field_code]: [
                                                        {
                                                            value_code: field.field_code,
                                                            value_text: e.target.checked ? "1" : "0",
                                                        },
                                                    ],
                                                }));
                                            }}
                                            className="mr-2"
                                        />
                                        {field.field_name}{" "}
                                        {field?.other_info_json?.required && <span className="text-red-500">*</span>}
                                    </label>
                                </div>
                            );
                        }

                        if (field.field_type === "Text") {
                            return (
                                <div key={field.labor_dimensions_id} className="mt-4">
                                    <label className="block font-semibold mb-1">
                                        {field.field_name}{" "}
                                        {field?.other_info_json?.required && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        value={localSelectedMap[field.field_code]?.[0]?.value_text || ""}
                                        onChange={(e) =>
                                            setLocalSelectedMap((prev) => ({
                                                ...prev,
                                                [field.field_code]: [
                                                    {
                                                        value_code: field.field_code,
                                                        value_text: e.target.value,
                                                    },
                                                ],
                                            }))
                                        }
                                        className="w-full p-2 border border-gray-300 rounded-md"
                                        placeholder={`Enter ${field.field_name}`}
                                    />
                                </div>
                            );
                        }

                        return null;
                    })}

                    <label className="text-gray-800 text-md font-medium mt-4">Note</label>
                    <textarea
                        rows={3}
                        className="w-full p-2 border border-gray-300 rounded-md"
                        placeholder="Enter note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />
                    {/* Submit button */}
                    {submitting ? (
                        <div className="flex justify-center mt-6">
                            <Loader size={24} color="#2664eb" strokeWidth={2.5} className="animate-spin" />
                        </div>
                    ) : locationState?.mode === "edit" ? (
                        <div className="flex mt-6 space-x-2">
                            <button
                                type="button"
                                onClick={handleBackNavigation}
                                className="flex-1 py-2 bg-gray-400 text-white rounded-md font-semibold hover:bg-gray-500"
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                className="flex-1 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700"
                                disabled={submitting}
                            >
                                Update
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="w-full mt-6 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700"
                            disabled={submitting}
                        >
                            Submit
                        </button>
                    )}

                    {/* Bottom sheet for nested tag selection */}
                    <AttendanceTagSelector
                        isOpen={showBottomSheet}
                        field={fieldStack[fieldStack.length - 1] || null}
                        selected={localSelectedMap[fieldStack[0]?.field_code || ""] || []}
                        attendanceTagsOptions={attendanceTagsOptions}
                        onClose={() => {
                            console.log("onClose called, setting showBottomSheet to false");
                            setShowBottomSheet(false);
                        }}
                        onSelect={handleTagOptionSelect}
                        fieldStack={fieldStack}
                        setFieldStack={setFieldStack}
                        localSelectedMap={localSelectedMap}
                        setLocalSelectedMap={setLocalSelectedMap}
                    />

                    {/* Break Manager Overlay */}
                    <BreakManagerOverlay
                        isOpen={showBreakManager}
                        breaks={breaks}
                        editIndex={editBreakIndex}
                        onClose={() => setShowBreakManager(false)}
                        onSave={(updatedBreaks) => {
                            setBreaks(updatedBreaks);
                            setShowBreakManager(false);
                            toast.success(`${updatedBreaks.length} breaks saved`);
                        }}
                    />
                </>
            )}
        </div>
    );
};

export default AddTimeSheetEntryComponent;
